import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import PrintModal from '../../../../components/PrintModal';
import ModalCaricoOrdiniFornitore from '../../../../components/ModalCaricoOrdiniFornitore';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatQuantity, formatCurrency } from '../../../../utils/formatters';
import { usePermesso } from '../../../../context/PermessiContext';
import { useBloccoRecordContesto } from '../../../../hooks/useBloccoRecordContesto';
import { useDirtyForm } from '../../../../hooks/useDirtyForm';
import { API_DOCS } from './SchedaOrdineFornitore.api';
import './SchedaOrdineFornitore.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoOrdineFornitore = 'BOZZA' | 'EMESSO' | 'CHIUSO' | 'ANNULLATO';

interface RigaOF {
  id: number;
  ordinePos: number;
  articoloId: number | null;
  descrizione: string;
  unitaMisura: string | null;
  quantita: number | string;
  prezzoUnitarioAtteso: number | string | null;
  quantitaRicevuta: number | string;
  note: string | null;
  rigaRdaId: number | null;
  articolo: { id: number; codice: string; descrizioneBase: string } | null;
  rigaRda: {
    id: number;
    rda: { id: number; numero: number; anno: number };
    ordine: { id: number; numero: string } | null;
    commessa: { id: number; numero: string; descrizione: string | null } | null;
  } | null;
}

interface LogEntry {
  id: number;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface OrdineFornitore {
  id: number;
  societaId: number;
  sedeId: number;
  fornitoreId: number;
  stato: StatoOrdineFornitore;
  numero: string | null;
  dataEmissione: string | null;
  fornitoreRagioneSociale: string | null;
  fornitorePartitaIva: string | null;
  note: string | null;
  createdAt: string;
  annullatoAt: string | null;
  fornitore: { id: number; ragioneSociale: string; partitaIva: string | null } | null;
  sede: { id: number; nome: string } | null;
  createdBy: { id: number; nome: string } | null;
  annullatoDa: { id: number; nome: string } | null;
  chiusoAt: string | null;
  motivoChiusura: string | null;
  chiusuraAutomatica: boolean;
  chiusoDa: { id: number; nome: string } | null;
  righe: RigaOF[];
  logs: LogEntry[];
  carichi: CaricoCollegato[];
}

// Movimento di magazzino che ha ricevuto righe di questo ordine (o suo storno)
interface CaricoCollegato {
  id: number;
  numero: number | null;
  anno: number | null;
  data: string;
  stato: 'BOZZA' | 'CONFERMATO' | 'STORNATO';
  numeroDocumentoEsterno: string | null;
  dataDocumentoEsterno: string | null;
  storno: boolean;
  righe: number;
}

interface SedeOption {
  id: number;
  nome: string;
}

// Riga in editing locale (quantità e prezzo come stringa per gli input)
interface RigaLocale {
  key: string;
  rigaRdaId: number | null;
  articoloId: number | null;
  codice: string | null;
  descrizione: string;
  unitaMisura: string | null;
  quantita: string;
  prezzo: string;
  note: string;
  rdaId: number | null;
  rdaEtichetta: string | null;
  ordineClienteId: number | null;
  ordineClienteNumero: string | null;
  commessaNumero: string | null;
  commessaDescrizione: string | null;
  ricevuta: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_ORDINE: Record<StatoOrdineFornitore, { label: string; cls: string }> = {
  BOZZA:     { label: 'Bozza',     cls: 'bop-badge-neutral' },
  EMESSO:    { label: 'Emesso',    cls: 'bop-badge-success' },
  CHIUSO:    { label: 'Chiuso',    cls: 'bop-badge-info' },
  ANNULLATO: { label: 'Annullato', cls: 'bop-badge-danger' },
};

function formatDateTimeIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('it-IT')} ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function etichettaRda(anno: number, numero: number): string {
  return `RDA ${anno}/${String(numero).padStart(4, '0')}`;
}

function rigaServerToLocale(r: RigaOF): RigaLocale {
  return {
    key: `srv-${r.id}`,
    rigaRdaId: r.rigaRdaId,
    articoloId: r.articoloId,
    codice: r.articolo?.codice ?? null,
    descrizione: r.descrizione,
    unitaMisura: r.unitaMisura,
    quantita: String(r.quantita ?? '').replace('.', ','),
    prezzo: r.prezzoUnitarioAtteso != null ? String(r.prezzoUnitarioAtteso).replace('.', ',') : '',
    note: r.note || '',
    rdaId: r.rigaRda?.rda?.id ?? null,
    rdaEtichetta: r.rigaRda?.rda ? etichettaRda(r.rigaRda.rda.anno, r.rigaRda.rda.numero) : null,
    ordineClienteId: r.rigaRda?.ordine?.id ?? null,
    ordineClienteNumero: r.rigaRda?.ordine?.numero ?? null,
    commessaNumero: r.rigaRda?.commessa?.numero ?? null,
    commessaDescrizione: r.rigaRda?.commessa?.descrizione ?? null,
    ricevuta: Number(r.quantitaRicevuta ?? 0),
  };
}

// Parse decimale italiano ("12,5" → 12.5) — null se stringa vuota o non valida
function parseDecimale(s: string): number | null {
  if (!s.trim()) return null;
  const n = Number(s.trim().replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

// Importo riga (quantità × prezzo) — null se uno dei due non è valido
function importoRiga(r: RigaLocale): number | null {
  const q = parseDecimale(r.quantita);
  const p = parseDecimale(r.prezzo);
  if (q === null || p === null) return null;
  return q * p;
}

// Campo label/valore in sola lettura
function Campo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="odf-scheda__campo">
      <span className="odf-scheda__campo-label">{label}</span>
      <span className="odf-scheda__campo-value">
        {value === null || value === undefined || value === '' ? (
          <span className="bop-text-muted">—</span>
        ) : (
          value
        )}
      </span>
    </div>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function SchedaOrdineFornitore() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermesso();

  const [ordine, setOrdine] = useState<OrdineFornitore | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);

  // Editing testata (note + sede)
  const [note, setNote] = useState('');
  const [sedeIdLocale, setSedeIdLocale] = useState('');
  const [testataSnapshot, setTestataSnapshot] = useState('');
  const [sedi, setSedi] = useState<SedeOption[]>([]);

  // Editing righe
  const [righe, setRighe] = useState<RigaLocale[]>([]);
  const [righeSnapshot, setRigheSnapshot] = useState('');

  const [salvandoTestata, setSalvandoTestata] = useState(false);
  const [salvandoRighe, setSalvandoRighe] = useState(false);
  const [azioneInCorso, setAzioneInCorso] = useState(false);

  const etichetta = ordine
    ? `Ordine fornitore ${ordine.numero ?? '(bozza)'}`
    : 'Ordine fornitore';
  const isBozza = ordine?.stato === 'BOZZA';
  const isEmesso = ordine?.stato === 'EMESSO';
  const isChiuso = ordine?.stato === 'CHIUSO';
  const canEdit = can('acquisti.ordini_fornitori', 'edit');
  // Il carico crea un movimento di magazzino: serve il permesso del magazzino
  const canCaricare = can('magazzino.movimenti', 'edit');
  const haRicevuto = (ordine?.righe || []).some(r => Number(r.quantitaRicevuta) > 0);
  const haResiduo = (ordine?.righe || []).some(r => Number(r.quantitaRicevuta) + 0.00005 < Number(r.quantita));
  const [modalCarico, setModalCarico] = useState(false);
  const canDelete = can('acquisti.ordini_fornitori', 'delete');
  const editabile = isBozza && canEdit;

  useBloccoRecordContesto({
    recordSocietaId: ordine?.societaId,
    label: etichetta,
  });

  const testataDirty =
    testataSnapshot !== '' && JSON.stringify({ note, sedeId: sedeIdLocale }) !== testataSnapshot;
  const righeDirty = righeSnapshot !== '' && JSON.stringify(righe) !== righeSnapshot;
  useDirtyForm(editabile && (testataDirty || righeDirty), etichetta);

  // Stampa / invio al fornitore: motore di stampa, modulo ORDINE_FORNITORE
  const [modalStampa, setModalStampa] = useState(false);
  const apriStampa = () => {
    if (editabile && (testataDirty || righeDirty)) {
      showToast("Salva le modifiche prima di stampare o inviare l'ordine", 'error');
      return;
    }
    setModalStampa(true);
  };

  // ── Caricamento ─────────────────────────────────────────────────────────────

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    try {
      // @api GET /api/ordini-fornitori/:id -> {"success":true,"data":{"id":"number","societaId":"number","sedeId":"number","fornitoreId":"number","stato":"BOZZA|EMESSO|CHIUSO|ANNULLATO","numero":"string|null","dataEmissione":"string|null","fornitoreRagioneSociale":"string|null","fornitorePartitaIva":"string|null","note":"string|null","createdAt":"string","annullatoAt":"string|null","fornitore":{"id":"number","ragioneSociale":"string","partitaIva":"string|null"},"sede":{"id":"number","nome":"string"},"createdBy":{"id":"number","nome":"string"},"annullatoDa":{"id":"number","nome":"string"},"chiusoAt":"string|null","motivoChiusura":"string|null","chiusuraAutomatica":"boolean","chiusoDa":{"id":"number","nome":"string"},"righe":[{"id":"number","ordinePos":"number","articoloId":"number|null","descrizione":"string","unitaMisura":"string|null","quantita":"number","prezzoUnitarioAtteso":"number|null","quantitaRicevuta":"number","note":"string|null","rigaRdaId":"number|null","articolo":{"id":"number","codice":"string","descrizioneBase":"string"},"rigaRda":{"id":"number","rda":{"id":"number","numero":"number","anno":"number"},"ordine":{"id":"number","numero":"string"},"commessa":{"id":"number","numero":"string","descrizione":"string"}}}],"logs":[{"id":"number","utenteName":"string","azione":"string","dettaglio":"string","createdAt":"string"}],"carichi":[{"id":"number","numero":"number|null","anno":"number|null","data":"string","stato":"string","numeroDocumentoEsterno":"string|null","dataDocumentoEsterno":"string|null","storno":"boolean","righe":"number"}]}}
      const r = await apiFetch<{ success: boolean; data?: OrdineFornitore; error?: string }>(
        `/api/ordini-fornitori/${id}`
      );
      if (!r.success || !r.data) {
        setErrore(typeof r.error === 'string' ? r.error : 'Ordine fornitore non trovato');
        setOrdine(null);
        return;
      }
      const d = r.data;
      setOrdine(d);
      setNote(d.note || '');
      setSedeIdLocale(String(d.sedeId));
      setTestataSnapshot(JSON.stringify({ note: d.note || '', sedeId: String(d.sedeId) }));
      const righeLocali = (d.righe || []).map(rigaServerToLocale);
      setRighe(righeLocali);
      setRigheSnapshot(JSON.stringify(righeLocali));
    } catch (err: unknown) {
      setErrore(estraiErrore(err, "Errore nel caricamento dell'ordine fornitore"));
      setOrdine(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  // Sedi della società (per il select in BOZZA — la sede determina la numerazione)
  useEffect(() => {
    if (!ordine || ordine.stato !== 'BOZZA' || !canEdit) return;
    let cancelled = false;
    (async () => {
      try {
        // @api GET /api/sedi?societaId= -> {"sedi":[{"id":"number","nome":"string","societaId":"number"}]}
        const d = await apiFetch<{ sedi: SedeOption[] }>(`/api/sedi?societaId=${ordine.societaId}`);
        if (!cancelled) setSedi(d.sedi || []);
      } catch {
        if (!cancelled) setSedi([]);
      }
    })();
    return () => { cancelled = true; };
  }, [ordine, canEdit]);

  // ── Azioni header ───────────────────────────────────────────────────────────

  const emetti = async () => {
    if (!ordine) return;
    if (testataDirty || righeDirty) {
      showToast("Salva le modifiche prima di emettere l'ordine", 'error');
      return;
    }
    const nomeSede = ordine.sede?.nome || 'della sede';
    if (!window.confirm(`Emettere l'ordine fornitore? Verrà assegnato il numero dal numeratore della sede "${nomeSede}".`)) return;
    setAzioneInCorso(true);
    try {
      // @api POST /api/ordini-fornitori/:id/emetti -> {"success":true,"data":{"stato":"EMESSO","numero":"string","dataEmissione":"string"}}
      const r = await apiFetch<{ success: boolean; data?: { stato: string; numero: string; dataEmissione: string } }>(
        `/api/ordini-fornitori/${ordine.id}/emetti`,
        { method: 'POST' }
      );
      showToast(`Ordine ${r.data?.numero || ''} emesso`, 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'emissione dell'ordine fornitore"), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const elimina = async () => {
    if (!ordine) return;
    if (!window.confirm('Eliminare la bozza? Le quantità torneranno disponibili nelle RDA di origine.')) return;
    setAzioneInCorso(true);
    try {
      // @api DELETE /api/ordini-fornitori/:id -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/ordini-fornitori/${ordine.id}`, { method: 'DELETE' });
      showToast('Bozza eliminata — le quantità tornano nelle RDA', 'success');
      navigate('/commerciale/passivo/ordini');
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'eliminazione dell'ordine fornitore"), 'error');
      setAzioneInCorso(false);
    }
  };

  const annulla = async () => {
    if (!ordine) return;
    const motivo = window.prompt(
      "Annullare l'ordine fornitore? Le quantità torneranno disponibili nelle RDA di origine.\nMotivo (facoltativo):"
    );
    if (motivo === null) return; // utente ha premuto Annulla
    setAzioneInCorso(true);
    try {
      // @api POST /api/ordini-fornitori/:id/annulla {"motivo":"string|null"} -> {"success":true,"data":{"stato":"ANNULLATO"}}
      await apiFetch<{ success: boolean; data?: { stato: string } }>(
        `/api/ordini-fornitori/${ordine.id}/annulla`,
        { method: 'POST', body: JSON.stringify({ motivo: motivo.trim() || null }) }
      );
      showToast('Ordine annullato — le quantità tornano nelle RDA', 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'annullo dell'ordine fornitore"), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const chiudiConResiduo = async () => {
    if (!ordine) return;
    const motivo = window.prompt(
      "Chiudere l'ordine senza ricevere il residuo? Le righe non ricevute restano tali.\nMotivo (obbligatorio):"
    );
    if (motivo === null) return;
    if (!motivo.trim()) {
      showToast('Indica il motivo della chiusura', 'error');
      return;
    }
    setAzioneInCorso(true);
    try {
      // @api POST /api/ordini-fornitori/:id/chiudi {"motivo":"string"} -> {"success":true,"data":{"stato":"CHIUSO"}}
      await apiFetch<{ success: boolean }>(`/api/ordini-fornitori/${ordine.id}/chiudi`, {
        method: 'POST',
        body: JSON.stringify({ motivo: motivo.trim() }),
      });
      showToast('Ordine chiuso con residuo', 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nella chiusura dell'ordine"), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const riapri = async () => {
    if (!ordine) return;
    if (!window.confirm("Riaprire l'ordine? Il residuo torna da ricevere.")) return;
    setAzioneInCorso(true);
    try {
      // @api POST /api/ordini-fornitori/:id/riapri -> {"success":true,"data":{"stato":"EMESSO"}}
      await apiFetch<{ success: boolean }>(`/api/ordini-fornitori/${ordine.id}/riapri`, { method: 'POST' });
      showToast('Ordine riaperto', 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nella riapertura dell'ordine"), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  // ── Salvataggio testata (note + sede) ───────────────────────────────────────

  const salvaTestata = async () => {
    if (!ordine) return;
    const sedeId = parseInt(sedeIdLocale, 10);
    if (!sedeId) {
      showToast('Seleziona una sede valida', 'error');
      return;
    }
    setSalvandoTestata(true);
    try {
      // @api PUT /api/ordini-fornitori/:id {"note":"string|null","sedeId":"number"} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/ordini-fornitori/${ordine.id}`, {
        method: 'PUT',
        body: JSON.stringify({ note: note.trim() || null, sedeId }),
      });
      showToast('Informazioni salvate', 'success');
      setTestataSnapshot(JSON.stringify({ note, sedeId: sedeIdLocale }));
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel salvataggio delle informazioni'), 'error');
    } finally {
      setSalvandoTestata(false);
    }
  };

  // ── Editing righe ───────────────────────────────────────────────────────────

  const setCampoRiga = (key: string, campo: 'quantita' | 'prezzo' | 'note', val: string) => {
    setRighe(prev => prev.map(r => (r.key === key ? { ...r, [campo]: val } : r)));
  };

  const rimuoviRiga = (key: string) => {
    setRighe(prev => prev.filter(r => r.key !== key));
  };

  const salvaRighe = async () => {
    if (!ordine) return;
    if (righe.length === 0) {
      showToast("L'ordine deve avere almeno una riga. Per eliminarlo tutto usa \"Elimina\".", 'error');
      return;
    }
    const payload: Array<{
      rigaRdaId: number | null;
      articoloId: number | null;
      descrizione: string;
      unitaMisura: string | null;
      quantita: number;
      prezzoUnitarioAtteso: number | null;
      note: string | null;
    }> = [];
    for (const r of righe) {
      const q = parseDecimale(r.quantita);
      if (q === null || q <= 0) {
        showToast(`Quantità non valida per la riga "${r.descrizione}"`, 'error');
        return;
      }
      let prezzo: number | null = null;
      if (r.prezzo.trim() !== '') {
        prezzo = parseDecimale(r.prezzo);
        if (prezzo === null || prezzo < 0) {
          showToast(`Prezzo atteso non valido per la riga "${r.descrizione}"`, 'error');
          return;
        }
      }
      payload.push({
        rigaRdaId: r.rigaRdaId,
        articoloId: r.articoloId,
        descrizione: r.descrizione,
        unitaMisura: r.unitaMisura,
        quantita: q,
        prezzoUnitarioAtteso: prezzo,
        note: r.note.trim() || null,
      });
    }
    setSalvandoRighe(true);
    try {
      // @api PUT /api/ordini-fornitori/:id {"righe":[{"rigaRdaId":"number|null","articoloId":"number|null","descrizione":"string","unitaMisura":"string|null","quantita":"number","prezzoUnitarioAtteso":"number|null","note":"string|null"}]} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/ordini-fornitori/${ordine.id}`, {
        method: 'PUT',
        body: JSON.stringify({ righe: payload }),
      });
      showToast('Righe ordine salvate', 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel salvataggio delle righe'), 'error');
    } finally {
      setSalvandoRighe(false);
    }
  };

  const quantitaLetta = (r: RigaLocale): string => {
    const q = parseDecimale(r.quantita);
    return q !== null ? formatQuantity(q) : r.quantita;
  };

  const prezzoLetto = (r: RigaLocale): React.ReactNode => {
    const p = parseDecimale(r.prezzo);
    return p !== null ? formatCurrency(p) : <span className="bop-text-muted">—</span>;
  };

  const totaleAtteso = useMemo(
    () => righe.reduce((acc, r) => acc + (importoRiga(r) ?? 0), 0),
    [righe]
  );

  const statoBadge = ordine ? (
    <span className={`bop-badge ${(STATO_ORDINE[ordine.stato] ?? { cls: 'bop-badge-neutral' }).cls}`}>
      {(STATO_ORDINE[ordine.stato] ?? { label: ordine.stato }).label}
    </span>
  ) : null;

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="odf-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">Caricamento ordine fornitore…</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (errore || !ordine) {
    return (
      <Layout>
        <div className="odf-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">Ordine fornitore non disponibile</h3>
              <p className="bop-empty-state__text">{errore || 'Ordine fornitore non trovato.'}</p>
              <BopBackLink to="/commerciale/passivo/ordini" label="Torna agli ordini fornitore" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const d = ordine;
  // Dopo l'emissione la tabella mostra anche ricevuto e residuo
  const conRicevuto = !isBozza;
  const nColonneRighe = (editabile ? 11 : 10) + (conRicevuto ? 2 : 0);
  const colonnePrimaImporto = conRicevuto ? 8 : 6;

  return (
    <Layout>
      <div className="odf-scheda">
        <BopBackLink to="/commerciale/passivo/ordini" label="Torna agli ordini fornitore" />

        {/* Header */}
        <header className="odf-scheda__header">
          <div className="odf-scheda__header-left">
            <h1 className="bop-page-title">{etichetta}</h1>
            {statoBadge}
          </div>
          <div className="odf-scheda__header-actions">
            <button
              type="button"
              className="bop-btn bop-btn-secondary"
              onClick={apriStampa}
              title="Anteprima, PDF o invio email al fornitore con il template di Impostazioni → Report"
            >
              Stampa / Invia
            </button>
            {isBozza && canEdit && (
              <button
                className="bop-btn bop-btn-primary"
                onClick={emetti}
                disabled={azioneInCorso}
                title="Assegna il numero dal numeratore della sede e emette l'ordine"
              >
                Emetti ordine
              </button>
            )}
            {isBozza && canDelete && (
              <button
                className="bop-btn bop-btn-danger"
                onClick={elimina}
                disabled={azioneInCorso}
                title="Elimina la bozza — le quantità tornano nelle RDA"
              >
                Elimina
              </button>
            )}
            {isEmesso && canCaricare && (
              <button
                className="bop-btn bop-btn-primary"
                onClick={() => setModalCarico(true)}
                disabled={azioneInCorso}
                title="Registra la merce ricevuta: crea il movimento di carico in magazzino (si possono includere altri ordini aperti dello stesso fornitore)"
              >
                Carica merce
              </button>
            )}
            {isEmesso && canEdit && haRicevuto && (
              <button
                className="bop-btn bop-btn-secondary"
                onClick={chiudiConResiduo}
                disabled={azioneInCorso}
                title="Chiude l'ordine senza ricevere le quantità residue"
              >
                Chiudi con residuo
              </button>
            )}
            {isChiuso && canEdit && haResiduo && (
              <button
                className="bop-btn bop-btn-secondary"
                onClick={riapri}
                disabled={azioneInCorso}
                title="Riapre l'ordine: il residuo torna da ricevere"
              >
                Riapri ordine
              </button>
            )}
            {isEmesso && canEdit && !haRicevuto && (
              <button
                className="bop-btn bop-btn-danger"
                onClick={annulla}
                disabled={azioneInCorso}
                title="Annulla l'ordine — le quantità tornano nelle RDA"
              >
                Annulla ordine
              </button>
            )}
          </div>
        </header>

        {/* Informazioni */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Informazioni</h2>
            {editabile && testataDirty && (
              <button
                className="bop-btn bop-btn-primary bop-btn-sm"
                onClick={salvaTestata}
                disabled={salvandoTestata}
              >
                {salvandoTestata ? 'Salvataggio…' : 'Salva'}
              </button>
            )}
          </div>
          <div className="bop-card__body">
            <div className="odf-scheda__grid">
              <Campo
                label="Fornitore"
                value={d.fornitoreRagioneSociale || d.fornitore?.ragioneSociale}
              />
              <Campo
                label="P.IVA fornitore"
                value={d.fornitorePartitaIva || d.fornitore?.partitaIva}
              />
              {editabile ? (
                <div className="odf-scheda__campo">
                  <span className="odf-scheda__campo-label">Sede</span>
                  <select
                    className="bop-select"
                    value={sedeIdLocale}
                    onChange={e => setSedeIdLocale(e.target.value)}
                  >
                    {sedi.length === 0 && d.sede && (
                      <option value={String(d.sede.id)}>{d.sede.nome}</option>
                    )}
                    {sedi.map(s => (
                      <option key={s.id} value={String(s.id)}>{s.nome}</option>
                    ))}
                  </select>
                  <span className="odf-scheda__hint">La sede determina la numerazione dell'ordine</span>
                </div>
              ) : (
                <Campo label="Sede" value={d.sede?.nome} />
              )}
              <Campo label="Stato" value={statoBadge} />
              <Campo
                label="Creato da / il"
                value={`${d.createdBy?.nome || '—'} · ${formatDateTimeIT(d.createdAt)}`}
              />
              <Campo
                label="Emesso il"
                value={d.dataEmissione ? formatDateTimeIT(d.dataEmissione) : null}
              />
              <Campo
                label="Annullato da / il"
                value={
                  d.annullatoAt
                    ? `${d.annullatoDa?.nome || '—'} · ${formatDateTimeIT(d.annullatoAt)}`
                    : null
                }
              />
              {d.chiusoAt && (
                <Campo
                  label="Chiuso il"
                  value={
                    d.chiusuraAutomatica
                      ? `${formatDateTimeIT(d.chiusoAt)} · ricevuto per intero`
                      : `${formatDateTimeIT(d.chiusoAt)} · ${d.chiusoDa?.nome || '—'} — ${d.motivoChiusura || 'nessun motivo'}`
                  }
                />
              )}
            </div>
            {editabile ? (
              <div className="odf-scheda__note-group">
                <span className="odf-scheda__campo-label">Note</span>
                <textarea
                  className="bop-input odf-scheda__note-textarea"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Note per il fornitore o per uso interno"
                  rows={3}
                />
              </div>
            ) : (
              <div className="odf-scheda__note-group">
                <Campo label="Note" value={d.note} />
              </div>
            )}
          </div>
        </div>

        {/* Righe */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Righe</h2>
            {editabile && (
              <button
                className="bop-btn bop-btn-primary bop-btn-sm"
                onClick={salvaRighe}
                disabled={salvandoRighe || !righeDirty}
              >
                {salvandoRighe ? 'Salvataggio…' : 'Salva righe'}
              </button>
            )}
          </div>
          <div className="bop-card__body">
            {righe.length === 0 ? (
              <div className="bop-empty-state">
                <p className="bop-empty-state__text">Nessuna riga nell'ordine.</p>
              </div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Pos</th>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th>U.M.</th>
                    <th className="bop-text-right">Quantità</th>
                    {conRicevuto && <th className="bop-text-right">Ricevuto</th>}
                    {conRicevuto && <th className="bop-text-right">Residuo</th>}
                    <th className="bop-text-right">Prezzo atteso</th>
                    <th className="bop-text-right">Importo</th>
                    <th>RDA di origine</th>
                    <th>Ordine cliente</th>
                    <th>Commessa</th>
                    {editabile && <th className="bop-table__th--actions"></th>}
                  </tr>
                </thead>
                <tbody>
                  {righe.map((r, i) => {
                    const imp = importoRiga(r);
                    return (
                      <tr key={r.key}>
                        <td>{i + 1}</td>
                        <td>{r.codice || <span className="bop-text-muted">—</span>}</td>
                        <td>{r.descrizione}</td>
                        <td>{r.unitaMisura || <span className="bop-text-muted">—</span>}</td>
                        <td className="bop-text-right">
                          {editabile ? (
                            <input
                              className="bop-input odf-scheda__qta-input"
                              value={r.quantita}
                              onChange={e => setCampoRiga(r.key, 'quantita', e.target.value)}
                            />
                          ) : (
                            quantitaLetta(r)
                          )}
                        </td>
                        {conRicevuto && (
                          <td className="bop-text-right">
                            {r.ricevuta > 0 ? formatQuantity(r.ricevuta) : <span className="bop-text-muted">—</span>}
                          </td>
                        )}
                        {conRicevuto && (
                          <td className="bop-text-right">
                            {(() => {
                              const residuo = (parseDecimale(r.quantita) ?? 0) - r.ricevuta;
                              return residuo > 0.00005
                                ? formatQuantity(residuo)
                                : <span className="bop-badge bop-badge-success">Ricevuto</span>;
                            })()}
                          </td>
                        )}
                        <td className="bop-text-right">
                          {editabile ? (
                            <input
                              className="bop-input odf-scheda__prezzo-input"
                              value={r.prezzo}
                              onChange={e => setCampoRiga(r.key, 'prezzo', e.target.value)}
                              placeholder="0,00"
                            />
                          ) : (
                            prezzoLetto(r)
                          )}
                        </td>
                        <td className="bop-text-right">
                          {imp !== null ? formatCurrency(imp) : <span className="bop-text-muted">—</span>}
                        </td>
                        <td>
                          {r.rdaId && r.rdaEtichetta ? (
                            <Link to={`/commerciale/passivo/rda/${r.rdaId}`}>{r.rdaEtichetta}</Link>
                          ) : (
                            <span className="bop-text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {r.ordineClienteId && r.ordineClienteNumero ? (
                            <Link to={`/commerciale/attivo/ordini/${r.ordineClienteId}`}>
                              {r.ordineClienteNumero}
                            </Link>
                          ) : (
                            <span className="bop-text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {r.commessaNumero ? (
                            <span
                              className="odf-scheda__commessa"
                              title={r.commessaDescrizione || undefined}
                            >
                              {r.commessaNumero}
                            </span>
                          ) : (
                            <span className="bop-text-muted">—</span>
                          )}
                        </td>
                        {editabile && (
                          <td className="bop-table__td--actions">
                            <button
                              className="bop-btn bop-btn-danger bop-btn-sm"
                              onClick={() => rimuoviRiga(r.key)}
                            >
                              Rimuovi
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={colonnePrimaImporto} className="bop-text-right odf-scheda__totale-label">
                      Totale atteso
                    </td>
                    <td className="bop-text-right odf-scheda__totale-value">
                      {formatCurrency(totaleAtteso)}
                    </td>
                    <td colSpan={nColonneRighe - colonnePrimaImporto - 1}></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>

        {/* Carichi di magazzino collegati */}
        {!isBozza && (
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">Carichi di magazzino</h2>
            </div>
            <div className="bop-card__body">
              {(d.carichi || []).length === 0 ? (
                <div className="bop-empty-state">Nessuna merce ricevuta per questo ordine.</div>
              ) : (
                <table className="bop-table bop-table--clickable">
                  <thead>
                    <tr>
                      <th>Movimento</th>
                      <th>Data</th>
                      <th>DDT fornitore</th>
                      <th className="bop-text-right">Righe dell'ordine</th>
                      <th>Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.carichi.map(c => (
                      <tr key={c.id} onClick={() => navigate(`/magazzino/movimenti/${c.id}`)}>
                        <td>{c.numero ? `${c.numero}/${c.anno}` : `#${c.id}`}{c.storno ? ' (storno)' : ''}</td>
                        <td>{new Date(c.data).toLocaleDateString('it-IT')}</td>
                        <td>
                          {c.numeroDocumentoEsterno || <span className="bop-text-muted">—</span>}
                          {c.dataDocumentoEsterno ? ` del ${new Date(c.dataDocumentoEsterno).toLocaleDateString('it-IT')}` : ''}
                        </td>
                        <td className="bop-text-right">{c.righe}</td>
                        <td>
                          <span className={`bop-badge ${c.stato === 'STORNATO' ? 'bop-badge-danger' : c.storno ? 'bop-badge-warning' : 'bop-badge-success'}`}>
                            {c.stato === 'STORNATO' ? 'Stornato' : c.storno ? 'Storno' : 'Confermato'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Log attività */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Log attività</h2>
          </div>
          <div className="bop-card__body">
            {(d.logs || []).length === 0 ? (
              <div className="bop-empty-state">Nessuna voce di log.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Data/ora</th>
                    <th>Utente</th>
                    <th>Azione</th>
                    <th>Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {d.logs.map(l => (
                    <tr key={l.id}>
                      <td>{formatDateTimeIT(l.createdAt)}</td>
                      <td>{l.utenteName}</td>
                      <td>{l.azione}</td>
                      <td>{l.dettaglio || <span className="bop-text-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {modalCarico && (
          <ModalCaricoOrdiniFornitore
            societaId={ordine.societaId}
            fornitoreId={ordine.fornitoreId}
            fornitoreNome={d.fornitoreRagioneSociale || d.fornitore?.ragioneSociale || undefined}
            ordineFornitoreId={ordine.id}
            onClose={() => setModalCarico(false)}
            onCreated={async () => {
              setModalCarico(false);
              await carica();
            }}
          />
        )}

        {modalStampa && (
          <PrintModal
            societaId={ordine.societaId}
            entitaType="ordine_fornitore"
            entitaId={ordine.id}
            entitaLabel={etichetta}
            onClose={() => setModalStampa(false)}
          />
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
